import React, { useEffect, useRef, useState } from "react";
import "./styles.css";
import api from "../../../../../services/api";
import { ConectarPlayneeConversasModal } from "../../../../security/Users/me/components/Canais/modals/ConectarPlayneeConversasModal";

type ChatModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  messages: any[];
  hasMore?: boolean;
  onLoadMore?: () => Promise<void>;
  onMensagemEnviada?: () => Promise<void>;
  title?: string;
  modalClassName?: string;
  telefoneCliente?: string;
};

const ChatModal: React.FC<ChatModalProps> = ({
  isOpen,
  onClose,
  messages,
  onMensagemEnviada,
  title = "Conversas",
  telefoneCliente = "",
}) => {
  const panelChatScrollRef = useRef<HTMLDivElement | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const [canalStatus, setCanalStatus] = useState<{ numero: string | null; connected: boolean } | null>(null);
  const [textoInicio, setTextoInicio] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState("");
  const [showConectar, setShowConectar] = useState(false);

  useEffect(() => {
    if (!isOpen || messages.length > 0) return;
    setSucesso(false);
    setErro("");
    setTextoInicio("");
    api.get("/marketing/me/playnee-conversas/meu-status")
      .then(({ data }) => setCanalStatus(data))
      .catch(() => setCanalStatus({ numero: null, connected: false }));
  }, [isOpen, messages.length]);

  async function handleEnviar() {
    if (!textoInicio.trim()) { setErro("Digite uma mensagem."); return; }
    if (!telefoneCliente) { setErro("Número do cliente não disponível."); return; }
    if (!canalStatus?.numero) { setErro("Nenhum canal WhatsApp conectado."); return; }
    setEnviando(true);
    setErro("");
    try {
      const { data: status } = await api.get("/marketing/me/playnee-conversas/meu-status");
      setCanalStatus(status);
      if (!status.numero) {
        setErro("Nenhum canal WhatsApp conectado.");
        return;
      }
      if (!status.connected) {
        setShowConectar(true);
        return;
      }
      await api.post("/marketing/playnee-conversas/enviar", {
        destinatario: telefoneCliente.replace(/\D/g, ""),
        texto: textoInicio.trim(),
        instancia: status.numero,
      });
      setSucesso(true);
      setTimeout(async () => {
        setSucesso(false);
        setTextoInicio("");
        if (onMensagemEnviada) await onMensagemEnviada();
      }, 2500);
    } catch (err: any) {
      setErro(err?.response?.data?.message || "Erro ao enviar mensagem.");
    } finally {
      setEnviando(false);
    }
  }

  if (!isOpen) return null;

  const semConversa = !messages || messages.length === 0;

  return (
    <div>
      <div className="custom-backdrop" onClick={onClose} />

      <div className="drawer-right">
        <div className="drawer-header">
          <h3 className="drawer-title">{title}</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="chat__page-panelchat" ref={panelChatScrollRef}>
          {!semConversa ? (
            <div className="chat-tabs-container">
              <div className="chat-tabs-header">
                {messages.map((conv, index) => (
                  <button
                    key={index}
                    className={`chat-tab-button ${activeTab === index ? "active" : ""}`}
                    onClick={() => setActiveTab(index)}
                  >
                    {conv.inbox_name}
                  </button>
                ))}
              </div>
              <div className="chat-tab-content">
                <iframe
                  src={messages[activeTab]?.link}
                  className="chat-iframe"
                  title="Conversation"
                  frameBorder="0"
                />
              </div>
            </div>
          ) : (
            <div style={{ padding: "24px 20px" }}>
              <p className="chat__empty-message" style={{ marginBottom: 20 }}>
                Este cliente não possui conversas ativas.
              </p>

              {canalStatus && canalStatus.numero && (
                <div style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                    <i className="fab fa-whatsapp" style={{ color: "#25d366", marginRight: 6 }} />
                    Iniciar conversa via WhatsApp
                  </div>

                  {telefoneCliente ? (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Enviar para: <strong style={{ color: "#111827" }}>{telefoneCliente}</strong>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#ef4444" }}>
                      Número do cliente não disponível.
                    </div>
                  )}

                  <textarea
                    rows={4}
                    placeholder="Digite a mensagem inicial..."
                    value={textoInicio}
                    onChange={(e) => setTextoInicio(e.target.value)}
                    disabled={enviando || sucesso || !telefoneCliente}
                    style={{
                      width: "100%",
                      borderRadius: 6,
                      border: "1px solid #d1d5db",
                      padding: "8px 10px",
                      fontSize: 13,
                      resize: "vertical",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />

                  {sucesso && (
                    <p style={{ color: "#25d366", fontSize: 13, fontWeight: 600, margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
                      <i className="fas fa-check-circle" /> Mensagem enviada com sucesso!
                    </p>
                  )}
                  {erro && !sucesso && (
                    <p style={{ color: "#ef4444", fontSize: 12, margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
                      <i className="fas fa-exclamation-circle" /> {erro}
                    </p>
                  )}

                  {canalStatus && !canalStatus.connected && (
                    <p style={{ color: "#b45309", fontSize: 12, margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
                      <i className="fas fa-exclamation-triangle" /> Seu WhatsApp está desconectado. Você precisa reconectar para enviar.
                    </p>
                  )}

                  {!sucesso && (
                    <button
                      onClick={handleEnviar}
                      disabled={enviando || !telefoneCliente}
                      style={{
                        background: telefoneCliente ? "#25d366" : "#d1d5db",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "8px 14px",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: (enviando || !telefoneCliente) ? "not-allowed" : "pointer",
                        opacity: enviando ? 0.7 : 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                      }}
                    >
                      <i className="fab fa-whatsapp" />
                      {enviando ? "Enviando..." : "Enviar mensagem"}
                    </button>
                  )}
                </div>
              )}

              {canalStatus && !canalStatus.numero && (
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 8 }}>
                  Você não possui um canal WhatsApp conectado. Conecte em <strong>Meu Perfil → Canais</strong>.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConectarPlayneeConversasModal
        isOpen={showConectar}
        onClose={() => setShowConectar(false)}
        onConnected={() => {
          setShowConectar(false);
          setCanalStatus((prev) => prev ? { ...prev, connected: true } : prev);
        }}
        numeroAtual={canalStatus?.numero ?? null}
      />
    </div>
  );
};

export default ChatModal;
